/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import React, { useMemo, useState } from 'react';
import { List } from 'semantic-ui-react';

import * as dto from 'client/dto';
import { AggregationCat, DiseaseSparkDataMap } from 'models/DiseaseModels';
import {
  DefaultSortOptionValue,
  DiseaseEventListGlobalViewSortOptions,
  DiseaseEventListLocationViewSortOptions,
  EventListSortOptions,
} from 'models/SortByOptions';
import { sort } from 'utils/arrayHelpers';
import { Geoname } from 'utils/constants';
import { isMobile } from 'utils/responsive';
import { containsNoCaseNoLocale } from 'utils/stringHelpers';

import { BdSearch } from 'components/_controls/BdSearch';
import NotFoundMessage from 'components/_controls/Misc/NotFoundMessage';
import { PanelDropdown } from 'components/_controls/PanelDropdown';
import DashboardToolbar from 'components/Navigationbar/DashboardToolbar';
import { IPanelProps, Panel } from 'components/Panel';
import { ActivePanel } from 'components/SidebarView/sidebar-types';

import EventListItem from './EventListItem';

export type EventListPanelProps = IPanelProps & {
  isStandAlone?: boolean;
  activePanel: ActivePanel;
  disactIds: dto.CountryDiseaseActivityKey;
  events: dto.GetEventListModel;
  sparkData?: DiseaseSparkDataMap;
  aggregationCat?: AggregationCat;
  geonameId?: number;
  isEventListLoading?: boolean;
  onEventSelected?: (event: dto.GetEventModel) => void;
};

const EventListPanel: React.FC<EventListPanelProps> = ({
  isStandAlone = true,
  activePanel,
  geonameId,
  disactIds,
  events,
  sparkData,
  aggregationCat = 'day',
  isMinimized = false,
  isEventListLoading = false,
  onEventSelected,
  onClose = undefined,
  onMinimize = undefined,
}) => {
  const [sortBy, setSortBy] = useState(DefaultSortOptionValue as string); // TODO: 597e3adc: right now we get away with the setter cast, because it thinks its a string
  const [searchText, setSearchText] = useState('');

  const sortOptions = useMemo(
    () =>
      isStandAlone
        ? EventListSortOptions
        : geonameId && geonameId !== Geoname.GLOBAL_VIEW
        ? DiseaseEventListLocationViewSortOptions
        : DiseaseEventListGlobalViewSortOptions,
    [isStandAlone, geonameId]
  );

  const domProcessedEvents = useMemo(() => {
    const filteredEvents = searchText.length
      ? (events?.eventsList || []).filter(e =>
          containsNoCaseNoLocale(e.eventInformation.title, searchText)
        )
      : events?.eventsList || [];
    const sortedEvents = sort({
      items: filteredEvents,
      sortOptions,
      sortBy,
    });
    return sortedEvents && sortedEvents.length ? (
      <List>
        {sortedEvents.map(event => (
          <EventListItem
            key={`${event.eventInformation.diseaseActivityKey.diseaseId}-${event.eventInformation.diseaseActivityKey.speciesId}-${event.eventInformation.diseaseActivityKey.countryId}`}
            isActive={
              !!disactIds &&
              event.eventInformation.diseaseActivityKey.countryId === disactIds.countryId &&
              event.eventInformation.diseaseActivityKey.diseaseId === disactIds.diseaseId &&
              event.eventInformation.diseaseActivityKey.speciesId === disactIds.speciesId
            }
            model={event}
            sparkData={
              sparkData && event && sparkData[event.eventInformation.diseaseActivityKey.countryId]
            }
            aggregationCat={aggregationCat}
            onEventSelected={onEventSelected}
            isStandAlone={isStandAlone}
          />
        ))}
      </List>
    ) : (
      <NotFoundMessage text="Event not found" />
    );
  }, [
    searchText,
    events,
    sortOptions,
    sortBy,
    disactIds,
    sparkData,
    onEventSelected,
    isStandAlone,
  ]);

  const isMobileDevice = isMobile(useBreakpointIndex());
  if (
    isMobileDevice &&
    activePanel !== 'EventListPanel' &&
    activePanel !== 'DiseaseEventListPanel'
  ) {
    return null;
  }

  return (
    <Panel
      isAnimated
      isLoading={isEventListLoading}
      title="All Activity"
      onClose={onClose}
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      toolbar={
        <>
          {isMobileDevice && isStandAlone && <DashboardToolbar showToolbar />}
          <PanelDropdown
            prefixLabel="Sort by"
            selectedValue={sortBy}
            options={sortOptions}
            onSelect={x => setSortBy(x)}
            disabled={isEventListLoading}
          />
          <BdSearch
            searchText={searchText}
            placeholder={isStandAlone ? 'Search for disease or country' : 'Search for country'}
            debounceDelay={500}
            onSearchTextChange={setSearchText}
            bottomBorderOnly
          />
        </>
      }
      isStandAlone={isStandAlone}
      canClose={!isStandAlone}
      canMinimize
    >
      {domProcessedEvents}
    </Panel>
  );
};

export default EventListPanel;
